import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { fmtDate } from "@/lib/credits";
import { toEmbedUrl, isVideoKind, fetchBlobUrl, downloadFile, registerExternal, parseYouTube } from "@/lib/resources";
import { YouTubePlayer, InvalidVideo } from "@/components/resources/YouTubePlayer";
import { fileTypeMeta } from "@/components/resources/ResourceThumb";
import { ResourceCard } from "@/components/resources/ResourceCard";
import { ArrowLeft, Download, Heart, Loader2, ExternalLink, Play, History, Info } from "lucide-react";

function InfoRow({ label, value }) {
  return <div className="flex items-center justify-between border-b border-white/[0.04] py-2.5 text-sm"><span className="text-neutral-500">{label}</span><span className="text-right font-medium text-white">{value}</span></div>;
}

export default function ResourceDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [res, setRes] = useState(null);
  const [loading, setLoading] = useState(true);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [thumbUrl, setThumbUrl] = useState(null);
  const [watching, setWatching] = useState(false);
  const erroredRef = useRef(false);
  const viewedRef = useRef(false);
  const playerRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/resources/${id}`);
      setRes(data);
    } catch (e) {
      if (!erroredRef.current) { erroredRef.current = true; toast.error("Resource not available"); navigate("/resources"); }
    }
    finally { setLoading(false); }
  }, [id, navigate]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    let created = null;
    if (res && res.has_file && (res.kind === "image" || res.file_type === "pdf")) {
      fetchBlobUrl(`/resources/${res.id}/preview`).then((u) => { created = u; setPreviewUrl(u); }).catch(() => {});
    }
    return () => { if (created) URL.revokeObjectURL(created); };
  }, [res]);

  useEffect(() => {
    let created = null;
    if (res && res.has_thumbnail && res.kind !== "image") {
      fetchBlobUrl(`/resources/${res.id}/thumbnail`).then((u) => { created = u; setThumbUrl(u); }).catch(() => {});
    }
    return () => { if (created) URL.revokeObjectURL(created); };
  }, [res]);

  const meta = res ? fileTypeMeta(res.file_type, res.kind) : null;
  const video = res && isVideoKind(res.kind);
  const canDownload = res && (res.has_file || res.external_url) && (res.allow_download || video || res.kind === "external");

  const doDownload = async () => {
    const isExternal = res.external_url && (!res.has_file || ["external", "youtube", "vimeo"].includes(res.kind));
    try {
      if (isExternal) {
        const url = await registerExternal(res.id);
        if (url) window.open(url, "_blank", "noopener");
      } else {
        await downloadFile(res.id, res.original_filename);
        toast.success("Download started");
      }
      setRes((r) => ({ ...r, download_count: r.download_count + 1 }));
    } catch (e) { toast.error(e.detail || formatApiError(e.response?.data?.detail) || "Download unavailable"); }
  };

  const recordView = async () => {
    if (viewedRef.current) return;
    viewedRef.current = true;
    try { await api.post(`/resources/${res.id}/view`); setRes((r) => ({ ...r, view_count: (r.view_count || 0) + 1 })); } catch (e) {}
  };

  const watch = async () => {
    setWatching(true);
    playerRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    await recordView();
    if (res.kind === "video_file") {
      try { const u = await fetchBlobUrl(`/resources/${res.id}/preview`); setPreviewUrl(u); } catch (e) {}
    }
  };

  const toggleFav = async () => {
    try { const { data } = await api.post(`/resources/${res.id}/favorite`); setRes((r) => ({ ...r, is_favorite: data.favorite })); }
    catch (e) { toast.error("Failed"); }
  };

  if (loading || !res) return <DashboardLayout title="Resource"><div className="flex items-center justify-center py-24 text-neutral-500"><Loader2 className="h-6 w-6 animate-spin" /></div></DashboardLayout>;

  const embed = toEmbedUrl(res.external_url);
  const yt = res.kind === "youtube" ? (res.youtube_id ? { id: res.youtube_id, isShort: res.youtube_short } : parseYouTube(res.external_url)) : null;

  return (
    <DashboardLayout title={res.title}>
      <button onClick={() => navigate("/resources")} className="mb-5 inline-flex items-center gap-2 text-sm text-neutral-400 hover:text-white" data-testid="back-btn"><ArrowLeft className="h-4 w-4" /> Back to Resource Center</button>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <div ref={playerRef} className="overflow-hidden rounded-2xl border border-white/[0.06] bg-[#141414]">
            {yt ? (
              <YouTubePlayer videoId={yt.id} isShort={yt.isShort} title={res.title} sourceUrl={res.external_url} playing={watching} onPlay={watch} />
            ) : (
            <div className="relative aspect-video w-full bg-gradient-to-br from-[#181818] to-[#0b0b0b]">
              {res.kind === "youtube" ? (
                <InvalidVideo url={res.external_url} />
              ) : video && (watching || res.kind !== "video_file") && embed && res.kind !== "video_file" ? (
                <iframe title={res.title} src={embed} className="h-full w-full" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen data-testid="video-embed" />
              ) : res.kind === "video_file" && previewUrl ? (
                <video src={previewUrl} controls className="h-full w-full" data-testid="video-player" />
              ) : res.kind === "image" && previewUrl ? (
                <img src={previewUrl} alt={res.title} className="h-full w-full object-contain" data-testid="image-preview" />
              ) : res.file_type === "pdf" && previewUrl ? (
                <iframe title={res.title} src={previewUrl} className="h-full w-full" data-testid="pdf-preview" />
              ) : thumbUrl ? (
                <div className="group relative h-full w-full">
                  <img src={thumbUrl} alt={res.title} className="h-full w-full object-contain" data-testid="thumb-preview" />
                  {(res.external_url || canDownload) && (
                    <button onClick={video && res.kind !== "video_file" ? watch : doDownload} className="absolute bottom-4 right-4 inline-flex items-center gap-2 rounded-lg bg-[#E30613] px-4 py-2 text-sm font-medium text-white opacity-0 shadow-lg transition-opacity group-hover:opacity-100 hover:bg-[#FF1C2A]" data-testid="thumb-open-btn">
                      {res.external_url && !res.has_file ? <><ExternalLink className="h-4 w-4" /> Open Link</> : video ? <><Play className="h-4 w-4" /> Watch</> : <><Download className="h-4 w-4" /> Download</>}
                    </button>
                  )}
                </div>
              ) : (
                <div className="flex h-full w-full flex-col items-center justify-center gap-3 text-neutral-500">
                  {meta && <meta.Icon className={`h-16 w-16 ${meta.color}`} strokeWidth={1.2} />}
                  {video && embed && (
                    <button onClick={watch} className="inline-flex items-center gap-2 rounded-lg bg-[#E30613] px-4 py-2 text-sm font-medium text-white hover:bg-[#FF1C2A]" data-testid="watch-btn"><Play className="h-4 w-4 fill-white" /> Watch Video</button>
                  )}
                  {res.is_placeholder && <p className="text-xs">Marketing file coming soon</p>}
                </div>
              )}
            </div>
            )}
          </div>

          <div className="mt-6">
            <h1 className="font-display text-2xl font-semibold text-white">{res.title}</h1>
            <p className="mt-3 whitespace-pre-line text-sm leading-relaxed text-neutral-400">{res.description || "No description provided."}</p>
            {res.tags?.length > 0 && (
              <div className="mt-4 flex flex-wrap gap-2">
                {res.tags.map((t) => <span key={t} className="rounded-full border border-white/[0.08] bg-white/[0.03] px-3 py-1 text-xs text-neutral-400">#{t}</span>)}
              </div>
            )}
          </div>

          {res.related?.length > 0 && (
            <div className="mt-10">
              <h3 className="mb-3 font-display text-lg font-semibold text-white">Related Resources</h3>
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-3">
                {res.related.map((r) => <ResourceCard key={r.id} resource={r} showFavorite={false} />)}
              </div>
            </div>
          )}
        </div>

        <div className="space-y-4">
          <div className="rounded-2xl border border-white/[0.06] bg-[#141414] p-5">
            <div className="flex gap-2">
              {canDownload && (
                <button onClick={video && res.kind !== "video_file" ? watch : doDownload} className="inline-flex flex-1 items-center justify-center gap-2 rounded-lg bg-[#E30613] px-4 py-2.5 text-sm font-medium text-white hover:bg-[#FF1C2A]" data-testid="detail-download-btn">
                  {res.kind === "external" ? <><ExternalLink className="h-4 w-4" /> Open Link</> : video && res.kind !== "video_file" ? <><Play className="h-4 w-4" /> Watch</> : <><Download className="h-4 w-4" /> Download</>}
                </button>
              )}
              <button onClick={toggleFav} className={`inline-flex items-center justify-center rounded-lg border px-4 py-2.5 ${res.is_favorite ? "border-[#E30613]/40 text-[#FF6069]" : "border-white/[0.1] text-neutral-400 hover:bg-white/[0.05]"}`} data-testid="detail-favorite-btn"><Heart className={`h-4 w-4 ${res.is_favorite ? "fill-[#FF6069]" : ""}`} /></button>
            </div>
            {res.kind === "youtube" && res.external_url && (
              <a href={res.external_url} target="_blank" rel="noopener noreferrer" onClick={recordView} className="mt-2 inline-flex w-full items-center justify-center gap-2 rounded-lg border border-white/[0.1] px-4 py-2.5 text-sm font-medium text-neutral-200 hover:bg-white/[0.05]" data-testid="watch-on-youtube-btn">
                <ExternalLink className="h-4 w-4" /> Watch on YouTube
              </a>
            )}
          </div>

          <div className="rounded-2xl border border-white/[0.06] bg-[#141414] p-5">
            <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-white"><Info className="h-4 w-4 text-neutral-400" /> File Information</div>
            <InfoRow label="Category" value={res.category} />
            <InfoRow label="Type" value={meta?.label} />
            <InfoRow label="Version" value={`v${res.version}`} />
            <InfoRow label="Uploaded" value={fmtDate(res.created_at)} />
            <InfoRow label="Last Updated" value={fmtDate(res.updated_at)} />
            <InfoRow label="Downloads" value={res.download_count} />
            {isVideoKind(res.kind) && <InfoRow label="Views" value={res.view_count} />}
            {res.original_filename && <InfoRow label="File" value={res.original_filename} />}
          </div>

          {res.versions?.length > 0 && (
            <div className="rounded-2xl border border-white/[0.06] bg-[#141414] p-5">
              <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-white"><History className="h-4 w-4 text-neutral-400" /> Version History</div>
              {res.versions.map((v, i) => (
                <div key={i} className="flex items-center justify-between border-b border-white/[0.04] py-2 text-sm last:border-0" data-testid={`version-${v.version}`}>
                  <span className="text-white">v{v.version} {v.is_current && <span className="ml-1 rounded bg-emerald-500/15 px-1.5 py-0.5 text-[10px] text-emerald-400">current</span>}</span>
                  <span className="text-xs text-neutral-500">{fmtDate(v.created_at)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
